import { RecordFilter, Relation } from '@/types/api';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { tagToColor } from '@/utils/function';
import { StyledFilter } from '@/components/Approval/RightContainer/ApprovalContent/TitleInfo/styles';
import React, { useEffect, useRef } from 'react';
import { modeToTitle } from '@/components/Approval/utils/functions';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { formatAsDate } from '@/utils/format';
import {
  Container,
  StyledLinkButton,
  ContentWrapper,
  TitleText,
  InfoWrapper,
  CreatorText,
  Divider,
  DateText,
  MobileTitleText,
  MobileLayout,
} from './styles';

type P = {
  item: Relation & { createdAt?: string; createBy?: string };
  isFocused?: boolean;
  onSelect?: (attachment: any) => void;
  searchKeyword?: string;
};

const Attachment = ({ item, isFocused, onSelect, searchKeyword }: P) => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext<RecordFilter>();
  const isApproval = item.type === 'approval';
  const containerRef = useRef<HTMLButtonElement>(null);
  const { isMobilePath } = usePlatform();

  // 포커스될 때 스크롤하여 화면에 보이도록
  useEffect(() => {
    if (isFocused && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [isFocused]);
  const handleLink = (e: MouseEvent) => {
    const isApproval = item.link;
    if (isMobilePath) {
      const sendData = JSON.stringify({
        actionType: 'MOVE_TAB',
        tabName: isApproval ? 'approval' : 'document',
        url: item.link,
        docId: item.id,
      });
      window?.ReactNativeWebView?.postMessage(sendData);
    } else {
      const targetUrl = !isApproval
        ? `${process.env.NEXT_PUBLIC_ORIGIN_URL}/talkflow/${item.id}`
        : item.link;
      window.open(targetUrl, '_blank');
    }
    e.stopPropagation();
  };

  const handleClick = () => {
    if (onSelect) {
      onSelect(item);
    } else {
      const current = getValues('relations');
      const exists = current.some((d) => d.id === item.id);

      if (!exists) {
        setValue('relations', [
          ...current,
          {
            type: isApproval ? 'approval' : 'talkFlow',
            id: item.id,
            name: item.name ?? '(제목 없는 문서)',
            link: isApproval
              ? item.link
              : `${process.env.NEXT_PUBLIC_ORIGIN_URL}/talkflow/${item.id}`,
          },
        ]);
      }
    }
  };

  const renderHighlightedName = (
    text: string,
    highlight: string | undefined,
  ) => {
    if (!highlight) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span
              key={i}
              style={{ color: 'var(--dk-accent, var(--Portal-Color-Point-color, #06C))' }}
            >
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </>
    );
  };

  return (
    <Container
      key={item.id}
      onClick={handleClick}
      isFocused={isFocused}
      ref={containerRef}
    >
      {isMobilePath ? (
        <div style={{ width: '100%' }}>
          <MobileLayout
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <MobileTitleText>
              {renderHighlightedName(
                item.name ?? t('(제목 없는 문서)'),
                searchKeyword,
              )}
            </MobileTitleText>
            <div>
              <StyledLinkButton onClick={handleLink} />
            </div>
          </MobileLayout>
          <MobileLayout
            style={{
              alignItems: 'center',
              paddingTop: '4px',
              fontSize: '12px',
              fontWeight: '400',
              lineHeight: '18px',
            }}
          >
            <CreatorText>{item.createBy}</CreatorText>
            <DateText style={{ justifyContent: 'right' }}>
              {formatAsDate(item.createdAt ?? '')}
            </DateText>
          </MobileLayout>
        </div>
      ) : (
        <ContentWrapper>
          <StyledFilter colorSet={tagToColor(item.type)}>
            {modeToTitle(item.type)}
          </StyledFilter>
          <TitleText>
            {renderHighlightedName(
              item.name ?? t('(제목 없는 문서)'),
              searchKeyword,
            )}
          </TitleText>
          <InfoWrapper>
            <CreatorText>{item.createBy}</CreatorText>
            <Divider />
            <DateText>{formatAsDate(item.createdAt ?? '')}</DateText>
            <StyledLinkButton onClick={handleLink} />
          </InfoWrapper>
        </ContentWrapper>
      )}
    </Container>
  );
};
export default Attachment;
